/**
 * Primed TNT cubes.
 *
 * A primed charge is an entity, not a block, so it needs its own mesh: a 1x1x1
 * TNT cube whose bottom face sits at the entity's position, exactly the block
 * model vanilla draws for `tnt` (the entity's 0.98 hitbox is physics only).
 *
 * The cube is built in the mesher's packed vertex format and handed to the
 * opaque voxel material, so it picks up the atlas, the block/sky light of the
 * cell it was lit in, and the engine's lighting and fog without a shader of its
 * own. Vanilla's flash — the texture alternating with a "brightened to
 * near-white" copy every 5 ticks, `fuse / 5 % 2` — rides on the mesher's
 * emissive meta bit: that branch renders the block self-lit and scaled up,
 * which is what a brightened copy of the texture looks like. Toggling it is a
 * couple of writes into the cube's own 24 vertices.
 */

import * as THREE from 'three';
import { BLOCKS, TNT_ID } from '../blocks';
import { META_EMISSIVE, META_SHIFT, packLightMeta, packTile, POS_SCALE, UV_SCALE } from '../mesher/greedy';
import type { World } from '../world/chunk';
import { isFlashing, type Charge } from '../world/tnt';

/** Face order used by the packed format: +X, -X, +Y, -Y, +Z, -Z. */
const FACE_NORMAL = [0, 1, 2, 3, 4, 5];
const AO = 255;

interface Cube {
  mesh: THREE.Mesh;
  geometry: THREE.BufferGeometry;
  /** auv, as the flash writes into it. */
  auv: THREE.BufferAttribute;
  /** The unflashed z word of each vertex (light | meta << META_SHIFT). */
  words: Uint16Array;
  /** Is the cube currently drawing its brightened texture? */
  flashing: boolean;
}

export class PrimedTntRenderer {
  readonly group = new THREE.Group();

  private readonly cubes = new Map<Charge, Cube>();

  constructor(
    private readonly material: THREE.Material,
    private readonly world: World,
  ) {
    this.group.frustumCulled = false;
  }

  /** How many cubes are drawing the brightened texture right now — the flash,
   *  as it actually reached the meshes. */
  get flashing(): number {
    let n = 0;
    for (const cube of this.cubes.values()) if (cube.flashing) n++;
    return n;
  }

  /** Sync the cubes to the charges: spawn, move, flash, despawn. */
  update(charges: readonly Charge[]): void {
    for (const [charge, cube] of this.cubes) {
      if (charges.indexOf(charge) >= 0) continue;
      this.group.remove(cube.mesh);
      cube.geometry.dispose();
      this.cubes.delete(charge);
    }
    for (const charge of charges) {
      let cube = this.cubes.get(charge);
      if (!cube) {
        cube = this.spawn(charge);
        this.cubes.set(charge, cube);
      }
      cube.mesh.position.set(charge.x, charge.y, charge.z);
      const flashing = isFlashing(charge.fuse);
      if (flashing !== cube.flashing) {
        cube.flashing = flashing;
        // only the z word carries the meta: the uvs stay put
        const words = cube.auv.array as Uint16Array;
        for (let v = 0; v < 24; v++) {
          const w = v * 4 + 2;
          words[w] = flashing ? cube.words[w] | (META_EMISSIVE << META_SHIFT) : cube.words[w];
        }
        cube.auv.needsUpdate = true;
      }
    }
  }

  private spawn(charge: Charge): Cube {
    const geo = this.geometry(charge.x, charge.y, charge.z);
    const mesh = new THREE.Mesh(geo, this.material);
    mesh.frustumCulled = false;
    this.group.add(mesh);
    const auv = geo.getAttribute('auv') as THREE.BufferAttribute;
    return {
      mesh,
      geometry: geo,
      auv,
      words: new Uint16Array(auv.array as Uint16Array),
      flashing: false,
    };
  }

  /** A cube lit for the cell the charge was lit in (vanilla lights an entity
   *  from the block it stands in) — one small geometry per charge, disposed
   *  when its blast goes off. */
  private geometry(x: number, y: number, z: number): THREE.BufferGeometry {
    const raw = this.world.getBlockLight(Math.floor(x), Math.floor(y), Math.floor(z));
    const sky = this.world.getSky(Math.floor(x), Math.floor(y), Math.floor(z));
    const light = sky | (raw << 4);

    const tiles = BLOCKS[TNT_ID].tiles;
    const apos = new Uint16Array(24 * 4);
    const auv = new Uint16Array(24 * 4);
    const acolor = new Uint8Array(24 * 4);
    // primed TNT is a full block: bottom at the entity's feet, centred on it
    const x0 = -0.5, x1 = 0.5, y0 = 0, y1 = 1, z0 = -0.5, z1 = 0.5;

    let v = 0;
    const quad = (
      pts: ReadonlyArray<readonly [number, number, number]>,
      uvs: ReadonlyArray<readonly [number, number]>,
      face: number,
    ): void => {
      const tile = tiles[face];
      const meta = FACE_NORMAL[face]; // the flash bit is OR'd in at runtime
      for (let k = 0; k < 4; k++) {
        const o = v * 4;
        const [px, py, pz] = pts[k];
        apos[o] = Math.round((px + 1) * POS_SCALE);
        apos[o + 1] = Math.round((py + 1) * POS_SCALE);
        apos[o + 2] = Math.round((pz + 1) * POS_SCALE);
        apos[o + 3] = packTile(tile);
        auv[o] = Math.round(uvs[k][0] * UV_SCALE);
        auv[o + 1] = Math.round(uvs[k][1] * UV_SCALE);
        auv[o + 2] = packLightMeta(light, meta);
        auv[o + 3] = 0;
        acolor[o] = 255;
        acolor[o + 1] = 255;
        acolor[o + 2] = 255;
        acolor[o + 3] = AO;
        v++;
      }
    };
    // winding and u/v follow the mesher's block-model quads, so the opaque
    // material's backface culling shows exactly the six outside faces
    quad([[x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]], [[1, 1], [0, 1], [0, 0], [1, 0]], 0);
    quad([[x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]], [[0, 1], [1, 1], [1, 0], [0, 0]], 1);
    quad([[x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0]], [[0, 1], [0, 0], [1, 0], [1, 1]], 2);
    quad([[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]], [[0, 0], [1, 0], [1, 1], [0, 1]], 3);
    quad([[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], [[0, 1], [1, 1], [1, 0], [0, 0]], 4);
    quad([[x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0]], [[1, 1], [0, 1], [0, 0], [1, 0]], 5);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('apos', new THREE.BufferAttribute(apos, 4));
    geo.setAttribute('auv', new THREE.BufferAttribute(auv, 4));
    geo.setAttribute('acolor', new THREE.BufferAttribute(acolor, 4, true));
    geo.setIndex(new THREE.BufferAttribute(new Uint16Array([
      0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7, 8, 9, 10, 8, 10, 11,
      12, 13, 14, 12, 14, 15, 16, 17, 18, 16, 18, 19, 20, 21, 22, 20, 22, 23,
    ]), 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 64);
    return geo;
  }
}
